import { CallAPI } from "../../apiManager/ApiCall";
import { universitiesURL } from "../../apiManager/APIConfig";

// Get all universities (admin only)
export const GetUniversitiesCall = async () => {
  try {
    // Cache-bust so GET never reuses a stale 304 body (missing fields after edits).
    const response = await CallAPI({
      url: `${universitiesURL}?_=${Date.now()}`,
      method: "GET",
    });
    // API returns { status, message, data: universities[] }; tolerate a bare array too.
    const raw = response?.data !== undefined ? response.data : response;
    const list = Array.isArray(raw) ? raw : [];
    return list.map((u) => ({
      ...u,
      max_profs: u.max_profs ?? u.maxProfs ?? null,
      max_users: u.max_users ?? u.maxUsers ?? null,
      storage_limit_bytes: u.storage_limit_bytes ?? null,
      storage_used_bytes: u.storage_used_bytes ?? 0,
      storage_percent: u.storage_percent ?? 0,
    }));
  } catch (error) {
    console.error("GetUniversitiesCall error", error);
    return [];
  }
};

// Add a university
export const AddUniversityCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}`,
      method: "POST",
      body,
    });
    return response;
  } catch (error) {
    console.error("AddUniversityCall error", error);
    return { status: 400 };
  }
};

// Edit a university
export const EditUniversityCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}`,
      method: "PUT",
      body,
    });
    return response;
  } catch (error) {
    console.error("EditUniversityCall error", error);
    return { status: 400 };
  }
};

// Delete a university
export const DeleteUniversityCall = async (id) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}`,
      method: "DELETE",
      body: { id },
    });
    return response;
  } catch (error) {
    console.error("DeleteUniversityCall error", error);
    return { status: 400 };
  }
};

// GET /api/universities/:university_id/users — get university users (optional role: student, prof, etc.)
export const GetUniversityUsersCall = async (universityId, role = null) => {
  if (!universityId) return [];
  try {
    const url = role
      ? `${universitiesURL}/${universityId}/users?role=${role}`
      : `${universitiesURL}/${universityId}/users`;
    const response = await CallAPI({ url, method: "GET" });
    return response?.data ?? response?.rows ?? (Array.isArray(response) ? response : []);
  } catch (error) {
    console.error("GetUniversityUsersCall error", error);
    return [];
  }
};

// Add a user (e.g. professor, student) to a university — POST /api/universities/users
export const AddUniversityUsersCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/users`,
      method: "POST",
      body,
    });
    return response;
  } catch (error) {
    console.error("AddUniversityUsersCall error", error);
    return { status: 400 };
  }
};

// Edit a university user — PUT /api/universities/users/:user_id
export const EditUniversityUserCall = async (userId, body) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/users/${userId}`,
      method: "PUT",
      body,
    });
    return response;
  } catch (error) {
    console.error("EditUniversityUserCall error", error);
    return { status: 400 };
  }
};

// Remove a university user — DELETE /api/universities/users/:user_id
export const RemoveUniversityUserCall = async (userId) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/users/${userId}`,
      method: "DELETE",
    });
    return response;
  } catch (error) {
    console.error("RemoveUniversityUserCall error", error);
    return { status: 400 };
  }
};

// Resend invitation email — POST /api/universities/users/:user_id/resend-invitation
export const ResendInvitationCall = async (userId) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/users/${userId}/resend-invitation`,
      method: "POST",
    });
    return response;
  } catch (error) {
    console.error("ResendInvitationCall error", error);
    return { status: 400 };
  }
};

// Resend credentials (new temp password + email) — POST /api/universities/users/:user_id/resend-credentials
export const ResendCredentialsCall = async (userId) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/users/${userId}/resend-credentials`,
      method: "POST",
    });
    return response;
  } catch (error) {
    console.error("ResendCredentialsCall error", error);
    return { status: 400 };
  }
};

// --- University admins (per university) ---

export const GetUniversityAdminsCall = async (universityId) => {
  if (!universityId) return [];
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/${universityId}/admins`,
      method: "GET",
    });
    const list = response?.data;
    return Array.isArray(list) ? list : [];
  } catch (error) {
    console.error("GetUniversityAdminsCall error", error);
    return [];
  }
};

export const AddUniversityAdminCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/admins`,
      method: "POST",
      body,
    });
    return response;
  } catch (error) {
    console.error("AddUniversityAdminCall error", error);
    return { status: 400 };
  }
};

export const RemoveUniversityAdminCall = async (userId) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/admins/${userId}`,
      method: "DELETE",
    });
    return response;
  } catch (error) {
    console.error("RemoveUniversityAdminCall error", error);
    return { status: 400 };
  }
};

// --- Accepted domains (per university) ---

export const GetAcceptedDomainsCall = async (universityId) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/${universityId}/accepted-domains`,
      method: "GET",
    });
    const list = response?.data;
    return Array.isArray(list) ? list : [];
  } catch (error) {
    console.error("GetAcceptedDomainsCall error", error);
    return [];
  }
};

export const AddAcceptedDomainCall = async (body) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/accepted-domains`,
      method: "POST",
      body,
    });
    return response;
  } catch (error) {
    console.error("AddAcceptedDomainCall error", error);
    return { status: 400 };
  }
};

export const EditAcceptedDomainCall = async (id, body) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/accepted-domains/${id}`,
      method: "PUT",
      body,
    });
    return response;
  } catch (error) {
    console.error("EditAcceptedDomainCall error", error);
    return { status: 400 };
  }
};

export const DeleteAcceptedDomainCall = async (id) => {
  try {
    const response = await CallAPI({
      url: `${universitiesURL}/accepted-domains/${id}`,
      method: "DELETE",
    });
    return response;
  } catch (error) {
    console.error("DeleteAcceptedDomainCall error", error);
    return { status: 400 };
  }
};

